/**
 * src/lib/nav/publicQuoteToken.js — read the public quote capability token from
 * a URL query string (quote e-signature S3b).
 *
 * The customer signing link is `<origin>/?quote_token=<public_token>`. Kept pure
 * + testable so the AuthGate branch (src/main.jsx) stays a thin one-liner. Only a
 * UUID-shaped token counts — a garbage param falls through to the normal auth
 * flow rather than rendering the public page (mirrors the server UUID gate).
 */

const UUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function getPublicQuoteToken(
  search = (typeof window !== 'undefined' ? window.location.search : ''),
) {
  try {
    const raw = new URLSearchParams(search || '').get('quote_token');
    if (raw && UUID_RX.test(raw)) return raw;
  } catch {
    /* malformed query string — treat as no token, fall through to normal auth */
  }
  return null;
}

/**
 * TICKET-1102: `&internal=1` marks the builder's internal preview — the real
 * customer page, opened by staff, that must not count as a client open. The
 * server only honours it with a verified staff JWT; the page uses it to send no
 * engagement events and disable sign/comment/configure (no writes from a preview).
 */
export function isInternalPreview(
  search = (typeof window !== 'undefined' ? window.location.search : ''),
) {
  try { return new URLSearchParams(search || '').get('internal') === '1'; } catch { return false; }
}

export function buildInternalPreviewLink(token, origin) {
  const link = buildPublicQuoteLink(token, origin);
  return link ? `${link}&internal=1` : null;
}

/**
 * Build the customer signing link from a quote's public_token (S3c). Round-trips
 * with getPublicQuoteToken. Returns null when no token is available.
 */
export function buildPublicQuoteLink(
  token,
  origin = (typeof window !== 'undefined' ? window.location.origin : ''),
) {
  if (!token) return null;
  return `${origin}/?quote_token=${encodeURIComponent(token)}`;
}

/**
 * Build the SHARE link (S3c, TICKET-593) — the URL the CEO actually copies/sends
 * to the client. Points at the pre-auth `quotes.public_preview_card` action
 * (lib/handlers/quotes/public.js), which returns real HTML with a per-quote
 * <title> + og: tags so WhatsApp/iMessage/Slack previews show "AC Nautitech —
 * Quotation · boat · date · rev n" instead of the bare domain — then redirects a
 * real browser straight to buildPublicQuoteLink's SPA URL. Distinct from
 * buildPublicQuoteLink on purpose: that one is the round-trip contract with
 * getPublicQuoteToken (the actual redirect target) and must keep pointing at
 * `/?quote_token=`, never at this action.
 */
export function buildPublicQuoteShareUrl(
  token,
  origin = (typeof window !== 'undefined' ? window.location.origin : ''),
) {
  if (!token) return null;
  return `${origin}/api/data?action=quotes.public_preview_card&token=${encodeURIComponent(token)}`;
}
